import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The "New contact list" panel — source, inspection, column roles, the time-zone choice and the
 * rules (P14-01), reworked for the customer-portal review of 2026-09-25, Contact lists section:
 *
 *   · the file's bytes are decoded here, so a file that is not UTF-8, a spreadsheet or an empty
 *     file is said to be so before anything is sent (CL #112, CL #114, CL #115,
 *     `contact-list-file.ts`); a file can be dropped on the zone (CL #116), which shows where
 *     keyboard focus is (CL #117);
 *   · "From an API" is not offered as if it worked: it is marked coming soon (CL #128);
 *   · a paste is inspected once typing stops, not on every keystroke, and a refused inspection is
 *     never asked again (CL #127);
 *   · a refusal is the API's own sentence, shown beside the field it names, and it goes away as
 *     soon as that input changes (CL #105, CL #106);
 *   · whether a value is spoken follows the role chosen for its column (CL #107); Save waits
 *     until exactly one column is the phone number and the source can be read, and says what is
 *     missing (CL #110, CL #111, CL #130);
 *   · what will be saved is counted — "3 of 8 people will be saved — 5 skipped" — with the
 *     skipped rows in a table of line, name and reason, under the member's own mapping (CL #120);
 *     time zones and languages that cannot be read are listed, and people with no zone can be
 *     given the workspace's zone as a visible choice, never silently (CL #118);
 *   · a duplicate name is warned about before it is saved (CL #123), the rules' numbers can be
 *     chosen (CL #122), and a filled form is never thrown away without asking (CL #132);
 *   · the inspection has its own query key, so saving does not send the whole file again
 *     (CL #136).
 *
 * AMENDED 2026-10-09 (design sync, the Claude Design export of 2026-10-09): the name is checked as it is
 * typed against the design's rule, up to 40 characters, with "Looks good." and a count; the sources are
 * "Upload a CSV" and "Type or paste" (the CRM source is no longer drawn); a paste says how many numbers
 * are ready and which lines to fix — from the API's own reading, not a second parser in the browser;
 * the columns are "What each column is", each with an example and what the choice does, and a sentence
 * on whether the list can be called; "Skip people called recently" picks one of seven windows and the
 * no-answer rule is the design's fixed three; opened from New campaign, the panel says so, offers "Back
 * to the campaign", and saves as "Save and use in campaign".
 *
 * AMENDED 2026-10-10 (design sync, the Claude Design export of 2026-10-10, version 1791632939188324): the list has a
 * FIXED FORMAT — the first row names the columns with exactly `phone, name, language, note`, only `phone` required,
 * any other column ignored — said in "What the list can contain" with "Download a sample CSV". The column-role
 * mapping is gone: the console maps the columns by those names (`fixedColumnsOf`) and shows them read-only under
 * "Columns found", each with an example and its tag, and a sentence on whether the list can be called. A paste must
 * start with that header row; the paste check says so on line 1, names a column it ignores, and quotes a language it
 * cannot read (the API leaves such a line out since 2026-10-10). Kept beyond the export, because they still matter:
 * a `time_zone` column is read silently (zone-from-number, 2026-10-10), the "call in … time" choice for people whose
 * row and number give no zone, and what saving keeps — the skipped rows, duplicates and unreadable numbers included.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Icon, Input, Segmented, Textarea, classes, type SegmentedOption } from "@wetalk/ui";
import { useEffect, useId, useState, type DragEvent, type ReactNode } from "react";

import { SwitchControl } from "../billing/switch-control.js";
import { CAMPAIGN_GLYPH } from "../campaigns/campaign-glyph.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, authSessionQuery, workspaceDetailQuery } from "../../api/query.js";
import type { ConsoleGateway, ContactColumnView, ContactListInspectionView } from "../../api/gateway.js";

import {
  DEFAULT_WITHIN_DAY,
  FORMAT_COLUMN,
  NEW_LIST_NAME_MAX,
  NO_ANSWER_LIMIT,
  RECENT_WINDOW,
  SAMPLE_CSV,
  SAMPLE_CSV_FILE_NAME,
  columnsNoteOf,
  countText,
  emptySourceText,
  fixedColumnsOf,
  foundColumnsOf,
  listNameCheck,
  pasteHeaderOf,
  pasteRowWhyOf,
  pasteSummaryText,
  readableSummaryText,
  recentRuleText,
  refusalOf,
  retryInspection,
  unrecognisedText,
  type PasteLine,
  type Refusal,
} from "./contact-list-copy.js";
import {
  LEGACY_ENCODING,
  NOT_UTF8_MESSAGE,
  decodeAs,
  readContactFile,
  type LegacyEncoding,
} from "./contact-list-file.js";

/** The two sources the design draws (2026-10-09: the CRM source is no longer drawn). */
type WorkingSource = "csv_upload" | "paste";

const SOURCE_OPTION: readonly SegmentedOption<WorkingSource>[] = [
  { value: "csv_upload", get label() { return portalText("operations", "upload_a_csv_cb85d3b6"); } },
  { value: "paste", get label() { return portalText("operations", "type_or_paste_4f4ffe2f"); } },
];

/** How many lines to fix a paste check lists, as the design does. */
const PASTE_SHOWN = 5;

/** How long typing must stop before a paste is inspected (CL #127). */
export const PASTE_SETTLE_MS = 500;

/** How many skipped rows the table shows before "and N more". */
const SKIPPED_SHOWN = 50;

/** The inspection's own key, apart from the lists' (CL #136). */
const INSPECTION_KEY = QUERY_KEY.contactListInspection[0];

/** The export's paste placeholder, its first row marked as required. */
const pastePlaceholder = (): string => portalText("shell", "merge.contacts.pastePlaceholder");

/** A text that stops changing for `delay` ms. */
function useSettled(value: string, delay: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return settled;
}

/** What a source holds under the list's format: the columns as the format maps them, and what saving would keep. */
export interface FixedInspection {
  readonly inspection: ReturnType<typeof useQuery<ContactListInspectionView>>;
  /** The columns found, mapped by the format's names (empty until the source is read). */
  readonly column: readonly ContactColumnView[];
  /** What saving would keep under that mapping — the API's own count; `undefined` while it is read. */
  readonly counted: ContactListInspectionView | undefined;
}

/**
 * Reads a source and counts it under the format's mapping: the API names the columns, the format maps them, and —
 * when that mapping differs from the API's proposal — the API counts again under it, so the numbers shown are the
 * numbers saved (CL #120). The second read has its own key (CL #136).
 */
export function useFixedInspection(
  gateway: ConsoleGateway,
  key: string,
  source: WorkingSource,
  sourceHandle: string,
  enabled: boolean,
): FixedInspection {
  const inspection = useQuery({
    queryKey: [key, source, sourceHandle],
    queryFn: (): Promise<ContactListInspectionView> => gateway.inspectContactSource(source, sourceHandle),
    enabled,
    retry: retryInspection,
    gcTime: 0,
  });
  const column = fixedColumnsOf(inspection.data?.column ?? []);
  const differs = inspection.data !== undefined && inspection.data.column.some((one, index) => column[index]?.role !== one.role);
  const preview = useQuery({
    queryKey: [key, source, sourceHandle, column.map((one) => `${one.sourceName}:${one.role}`).join("|")],
    queryFn: (): Promise<ContactListInspectionView> => gateway.inspectContactSource(source, sourceHandle, column),
    enabled: enabled && differs,
    retry: retryInspection,
    gcTime: 0,
    placeholderData: keepPreviousData,
  });
  return { inspection, column, counted: differs ? preview.data : inspection.data };
}

export interface NewContactListPanelProps {
  /** The names already used in this workspace: a new list's name must differ (CL #123, design 2026-10-09). */
  readonly existingName: readonly string[];
  /** Opened from New campaign: the banner, "Back to the campaign" and "Save and use in campaign". */
  readonly forCampaign: boolean;
  readonly onBackToCampaign: () => void;
  readonly onClose: () => void;
  readonly onSaved: (saved: { readonly name: string; readonly contactListId: string }) => void;
}

/** "What the list can contain" — the export's format panel, with the sample file. */
export function ListFormat({ id }: { readonly id: string }): ReactNode {
  usePortalTranslation("shell");
  return (
    <div id={id} className="cl-format">
      <span className="cl-format-title">{portalText("shell", "merge.contacts.formatTitle")}</span>
      <span>{portalText("shell", "merge.contacts.formatIntro")}{" "}<strong>phone, name, language, note</strong>{portalText("shell", "merge.contacts.formatEnd")}{" "}</span>
      <span className="cl-format-grid">
        {FORMAT_COLUMN.map((one) => (
          <span key={one.name} className="cl-format-row">
            <strong>{one.name}</strong>
            <span>{one.help}</span>
          </span>
        ))}
      </span>
      <span>{portalText("shell", "merge.contacts.ignoredColumns")}{" "}
        <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(SAMPLE_CSV)}`} download={SAMPLE_CSV_FILE_NAME}>{portalText("operations", "download_a_sample_csv_89e88481")}{" "}</a>
      </span>
    </div>
  );
}

/**
 * The paste check (export 2026-10-10): the header row first, then the rows the API could not read, in the export's
 * words. `null` while nothing is typed, or while the API has not answered and the header row is fine.
 */
export function PasteCheck({
  id,
  text,
  counted,
}: {
  readonly id: string;
  readonly text: string;
  readonly counted: ContactListInspectionView | undefined;
}): ReactNode {
  usePortalTranslation("shell");
  const header = pasteHeaderOf(text);
  if (header === null) return null;
  if (header.named && (counted === undefined || counted.rowCount === 0)) {
    return header.line === null ? null : <PasteLines id={id} line={[header.line]} readable={0} />;
  }
  const line: PasteLine[] = header.line === null ? [] : [header.line];
  if (header.named && counted !== undefined) {
    line.push(...counted.unreadableRow.map((one) => ({ line: one.line, why: pasteRowWhyOf(one, counted.unrecognisedValue) })));
  }
  return <PasteLines id={id} line={line} readable={header.named && counted !== undefined ? counted.readableCount : 0} />;
}

function PasteLines({ id, line, readable }: { readonly id: string; readonly line: readonly PasteLine[]; readonly readable: number }): ReactNode {
  usePortalTranslation("shell");
  const allOk = line.length === 0 && readable > 0;
  return (
    <div id={id} role="status" className="cl-paste-check">
      <span className={classes("cl-paste-summary", !allOk && "cl-paste-summary-fix")}>
        <Icon glyph={allOk ? CAMPAIGN_GLYPH.checkCircle : CAMPAIGN_GLYPH.warningCircle} size="md" />
        {pasteSummaryText(readable, line.length, PASTE_SHOWN)}
      </span>
      {line.slice(0, PASTE_SHOWN).map((one) => (
        <span key={`${String(one.line)}-${one.why}`} className="cl-paste-line">
          <span className="cl-paste-line-n">{portalText("operations", "line_ea967600")}{" "}{one.line}</span>
          <span>{one.why}</span>
        </span>
      ))}
    </div>
  );
}

export function NewContactListPanel({
  existingName,
  forCampaign,
  onBackToCampaign,
  onClose,
  onSaved,
}: NewContactListPanelProps): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const nameHelpId = useId();
  const saveNoteId = useId();
  const formatId = useId();
  const pasteId = useId();
  const pasteCheckId = useId();

  const [name, setName] = useState("");
  const [source, setSource] = useState<WorkingSource>("csv_upload");
  const [fileName, setFileName] = useState("");
  const [fileText, setFileText] = useState("");
  const [fileNotice, setFileNotice] = usePortalMessage();
  const [notUtf8, setNotUtf8] = useState<Uint8Array | null>(null);
  const [dragging, setDragging] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const settledPaste = useSettled(pasteText, PASTE_SETTLE_MS);
  const [withinDayOn, setWithinDayOn] = useState(true);
  const [withinDay, setWithinDay] = useState(DEFAULT_WITHIN_DAY);
  const [afterNoAnswerOn, setAfterNoAnswerOn] = useState(false);
  const [assumeZone, setAssumeZone] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const sourceHandle = source === "csv_upload" ? fileText : settledPaste;
  // A paste is read only once its first row names the columns, phone among them (export 2026-10-10).
  const pasteHeader = source === "paste" ? pasteHeaderOf(settledPaste) : null;
  const attached = sourceHandle.trim() !== "" && (source === "csv_upload" || pasteHeader?.named === true);

  // The zone offered for people with none: the workspace's, named on screen (CL #118).
  const authSession = useQuery(authSessionQuery(gateway));
  const workspaceId = authSession.data?.workspaceId ?? "";
  const workspace = useQuery({ ...workspaceDetailQuery(gateway, workspaceId), enabled: workspaceId !== "" });
  const workspaceZone = workspace.data?.timeZone ?? null;

  const { inspection, column, counted } = useFixedInspection(gateway, INSPECTION_KEY, source, sourceHandle, attached);

  const nameCheck = listNameCheck(name, existingName);
  const zoneMapped = column.some((one) => one.role === "time_zone");
  const phoneNamed = column.some((one) => one.role === "e164");

  const create = useMutation({
    mutationFn: () =>
      gateway.createContactList({
        name: name.trim(),
        source,
        sourceHandle,
        column,
        skipCalledWithinDay: withinDayOn ? withinDay : 0,
        skipAfterNoAnswer: afterNoAnswerOn ? NO_ANSWER_LIMIT : 0,
        assumedTimeZone: assumeZone && workspaceZone !== null ? workspaceZone : null,
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.contactList });
      onSaved({ name: saved.name, contactListId: saved.contactListId });
    },
  });

  /** Any change to what would be saved clears the last refusal (CL #105). */
  const changed = (): void => {
    if (create.isError) create.reset();
  };

  const refusal: Refusal | null = create.error === null ? null : refusalOf(create.error);
  const inspectionRefusal: Refusal | null = inspection.error === null ? null : refusalOf(inspection.error);

  const problem: string[] = [];
  if (name.trim() === "") problem.push(portalText("operations", "give_the_list_a_name_a0abb5a8"));
  if (nameCheck.invalid) problem.push(portalText("shell", "merge.contacts.fixName", { message: nameCheck.message }));
  if (sourceHandle.trim() === "") problem.push(source === "csv_upload" ? portalText("operations", "choose_a_csv_file_d5e375fd") : portalText("shell", "merge.contacts.pastePeople"));
  else if (source === "paste" && pasteHeader?.named !== true) problem.push(portalText("shell", "merge.contacts.pasteHeaderProblem"));
  if (attached && inspection.isError) problem.push(portalText("operations", "choose_a_file_that_can_be_read_3387687b"));
  if (attached && counted === undefined && !inspection.isError) problem.push(portalText("shell", "merge.contacts.waitRead"));
  if (inspection.data !== undefined) {
    if (inspection.data.rowCount === 0) problem.push(portalText("operations", "add_people_to_it_there_is_nobody_in_it_yet_823289a3"));
    else if (!phoneNamed) problem.push(portalText("shell", "merge.contacts.phoneNameProblem"));
    else if (counted !== undefined && counted.readableCount === 0) problem.push(portalText("operations", "no_row_has_a_phone_number_that_can_be_read_0d3d5e11"));
  }
  const saveable = problem.length === 0 && !create.isPending;

  const dirty = name.trim() !== "" || fileName !== "" || pasteText.trim() !== "";
  const cancel = (): void => {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const takeFile = (chosen: File | undefined): void => {
    changed();
    setFileText("");
    setFileNotice(null);
    setNotUtf8(null);
    setFileName(chosen === undefined ? "" : chosen.name);
    if (chosen === undefined) return;
    readContactFile(chosen).then(
      (reading) => {
        if (reading.kind === "text") setFileText(reading.text);
        else if (reading.kind === "refused") setFileNotice(reading.message);
        else setNotUtf8(reading.byte);
      },
      () => {
        setFileNotice(portalText("operations", "that_file_could_not_be_read_by_the_browser_so_nothing_was_9b047bd6"));
      },
    );
  };

  const readAs = (encoding: LegacyEncoding): void => {
    if (notUtf8 === null) return;
    setFileText(decodeAs(notUtf8, encoding));
    setNotUtf8(null);
  };

  const fileDragged = (event: DragEvent): boolean => Array.from(event.dataTransfer.types).includes("Files");

  const dropLabel =
    fileName === ""
      ? portalText("operations", "drop_a_csv_here_or_browse_9904bdac")
      : inspection.data === undefined
        ? fileName
        : portalText("shell", "merge.contacts.fileRows", { name: fileName, count: inspection.data.rowCount, figure: countText(inspection.data.rowCount) });

  return (
    <div
      className="c-panel c-panel-outlined cl-new"
      // A file dropped anywhere on the panel is not opened by the browser in place of the page
      // (CL #116): only the drop zone reads it.
      onDragOver={(event) => {
        if (fileDragged(event)) event.preventDefault();
      }}
      onDrop={(event) => {
        if (fileDragged(event)) event.preventDefault();
      }}
    >
      {forCampaign ? (
        <p className="cl-from-campaign">
          <Icon glyph={CAMPAIGN_GLYPH.arrowBendUpLeft} size="lg" />
          <span className="cl-from-campaign-text">{portalText("operations", "making_a_list_for_your_new_campaign_when_you_save_you_go_b_c1f3dd92")}{" "}</span>
          <Button variant="ghost" onClick={onBackToCampaign}>{portalText("operations", "back_to_the_campaign_367b1404")}{" "}</Button>
        </p>
      ) : null}
      <p className="c-section-title cl-new-title">{portalText("operations", "new_contact_list_20ea6f83")}</p>

      <div className="cl-new-grid">
        <div className="cl-new-column">
          <div className="field">
            <label htmlFor={`${nameHelpId}-input`}>{portalText("operations", "list_name_63fb0d7f")}</label>
            <Input
              id={`${nameHelpId}-input`}
              value={name}
              maxLength={NEW_LIST_NAME_MAX}
              placeholder={portalText("operations", "october_customers_ecfa6860")}
              aria-invalid={nameCheck.invalid || refusal?.place === "name"}
              aria-describedby={refusal?.place === "name" ? `${nameHelpId} ${nameHelpId}-error` : nameHelpId}
              onChange={(event) => {
                changed();
                setName(event.currentTarget.value);
              }}
            />
            <span
              id={nameHelpId}
              role="status"
              className={classes(
                "cl-name-help",
                nameCheck.invalid ? "cl-name-help-invalid" : nameCheck.message !== "" && "cl-name-help-ok",
              )}
            >
              <span>{nameCheck.message}</span>
              <span className="cl-name-count">
                {name.length}/{NEW_LIST_NAME_MAX}
              </span>
            </span>
            {refusal?.place === "name" ? (
              <span id={`${nameHelpId}-error`} className="wt-field-error" role="alert">
                {refusal.message}
              </span>
            ) : null}
          </div>

          <Field label={portalText("operations", "where_it_comes_from_799d8f14")}>
            {() => (
              <Segmented
                name="contactSource"
                legend={portalText("operations", "where_it_comes_from_799d8f14")}
                value={source}
                options={SOURCE_OPTION}
                onChange={(next) => {
                  changed();
                  setSource(next);
                }}
              />
            )}
          </Field>

          <ListFormat id={formatId} />

          {source === "csv_upload" ? (
            <div className="field">
              <span className="cl-field-label">{portalText("operations", "csv_file_9e185822")}</span>
              <label
                className="c-drop cl-drop"
                data-attached={attached}
                data-dragging={dragging}
                onDragOver={(event) => {
                  if (!fileDragged(event)) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "copy";
                  setDragging(true);
                }}
                onDragLeave={() => {
                  setDragging(false);
                }}
                onDrop={(event) => {
                  if (!fileDragged(event)) return;
                  event.preventDefault();
                  event.stopPropagation();
                  setDragging(false);
                  takeFile(event.dataTransfer.files[0]);
                }}
              >
                <input
                  type="file"
                  accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                  className="c-visually-hidden"
                  aria-describedby={formatId}
                  onChange={(event) => {
                    const picked = event.currentTarget.files?.[0];
                    // Cleared so that picking the SAME file again (re-saved as CSV UTF-8 after a
                    // refusal, under the same name) fires `change` and is read again (a3b9010).
                    event.currentTarget.value = "";
                    takeFile(picked);
                  }}
                />
                <span className="c-drop-name">{dropLabel}</span>
                <span className="c-drop-hint">{portalText("operations", "one_person_per_row_a_name_and_a_phone_column_is_enough_a1d8ff0b")}</span>
              </label>
              {fileNotice === null ? null : (
                <p className="c-note c-note-critical" role="alert">
                  {fileNotice}
                </p>
              )}
              {notUtf8 === null ? null : (
                <div className="c-note c-note-critical cl-encoding" role="alert">
                  <p>{NOT_UTF8_MESSAGE()}</p>
                  <div className="c-row">
                    {LEGACY_ENCODING.map((one) => (
                      <Button
                        key={one.value}
                        variant="secondary"
                        onClick={() => {
                          readAs(one.value);
                        }}
                      >
                        {one.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="field cl-paste-field">
              <label htmlFor={pasteId}>{portalText("shell", "merge.contacts.pasteLabel")}</label>
              <Textarea
                id={pasteId}
                value={pasteText}
                rows={5}
                aria-describedby={`${formatId} ${pasteCheckId}`}
                placeholder={pastePlaceholder()}
                onChange={(event) => {
                  changed();
                  setPasteText(event.currentTarget.value);
                }}
              />
              <PasteCheck id={pasteCheckId} text={settledPaste} counted={attached ? counted : undefined} />
            </div>
          )}
          {refusal?.place === "source" ? (
            <p className="c-note c-note-critical" role="alert">
              {refusal.message}
            </p>
          ) : null}
        </div>

        <div className="cl-new-column">
          {attached && inspectionRefusal !== null ? (
            <p className="c-note c-note-critical" role="alert">
              {inspectionRefusal.message}
            </p>
          ) : null}

          {attached && inspection.data === undefined && !inspection.isError ? (
            <p className="c-note">{portalText("operations", "reading_what_is_in_it_cfde9ca5")}</p>
          ) : null}

          {inspection.data === undefined || !attached ? null : inspection.data.rowCount === 0 ? (
            <p className="c-note c-note-critical" role="alert">
              {emptySourceText(source)}
            </p>
          ) : source === "paste" ? null : (
            <InspectionResult
              column={column}
              counted={counted}
              columnRefusal={refusal?.place === "column" ? refusal.message : null}
            />
          )}

          {inspection.data === undefined || inspection.data.rowCount === 0 || !attached ? null : (
            <div className="field">
              <span className="cl-field-label">{portalText("operations", "time_zones_fa9aac40")}</span>
              {zoneMapped ? null : (
                <p className="c-note">{portalText("shell", "merge.contacts.zoneExplanation")}{" "}</p>
              )}
              {workspaceZone === null ? null : (
                <label className="cl-check">
                  <input
                    type="checkbox"
                    checked={assumeZone}
                    onChange={(event) => {
                      changed();
                      setAssumeZone(event.currentTarget.checked);
                    }}
                  />
                  <span>
                    {portalText("shell", "merge.contacts.missingZone")} {workspaceZone}{" "}{portalText("operations", "time_714eea0f")}{" "}</span>
                </label>
              )}
            </div>
          )}

          <div className="field">
            <span className="cl-field-label">{portalText("operations", "do_not_call_4e934f47")}</span>
            <div className="cl-rules">
              {/* Not a switch you can turn off: a campaign can never include a suppressed number (AC-31). */}
              <span className="cl-rule">
                <span className="cl-rule-body">
                  <span className="cl-rule-title">{portalText("operations", "skip_the_do_not_call_list_b6ba8ae5")}</span>
                  <span className="cl-rule-dek">{portalText("operations", "anyone_who_has_asked_not_to_be_called_is_left_out_896a3795")}</span>
                </span>
                <SwitchControl
                  label={portalText("operations", "skip_the_do_not_call_list_b6ba8ae5")}
                  labelVisible={false}
                  checked
                  disabled
                  onChange={() => {
                    // Deliberately inert: a campaign can never include them.
                  }}
                />
              </span>
              <span className="cl-rule">
                <span className="cl-rule-body">
                  <span className="cl-rule-title">{portalText("operations", "skip_people_called_recently_49bdd8c0")}</span>
                  <span className="cl-rule-dek">{recentRuleText(withinDayOn, withinDay)}</span>
                </span>
                <SwitchControl
                  label={portalText("operations", "skip_people_called_recently_49bdd8c0")}
                  labelVisible={false}
                  checked={withinDayOn}
                  onChange={(next) => {
                    changed();
                    setWithinDayOn(next);
                  }}
                />
              </span>
              {withinDayOn ? (
                <div role="radiogroup" aria-label={portalText("operations", "how_far_back_89553001")} className="cl-windows">
                  {RECENT_WINDOW.map((one) => (
                    <button
                      key={one.day}
                      type="button"
                      role="radio"
                      aria-checked={withinDay === one.day}
                      className={classes("cl-window", withinDay === one.day && "cl-window-on")}
                      onClick={() => {
                        changed();
                        setWithinDay(one.day);
                      }}
                    >
                      {one.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <span className="cl-rule">
                <span className="cl-rule-body">
                  <span className="cl-rule-title">{portalText("operations", "skip_numbers_with_no_answer_three_times_0f7f766f")}</span>
                  <span className="cl-rule-dek">{portalText("operations", "stops_chasing_dead_numbers_b3694cca")}</span>
                </span>
                <SwitchControl
                  label={portalText("operations", "skip_numbers_with_no_answer_three_times_0f7f766f")}
                  labelVisible={false}
                  checked={afterNoAnswerOn}
                  onChange={(next) => {
                    changed();
                    setAfterNoAnswerOn(next);
                  }}
                />
              </span>
            </div>
          </div>
        </div>
      </div>

      {refusal?.place === "other" ? (
        <p className="c-note c-note-critical" role="alert">
          {refusal.message}
        </p>
      ) : null}

      <div className="cl-new-foot">
        {problem.length === 0 ? null : (
          <p className="cl-save-note" id={saveNoteId}>
            {portalText("shell", "merge.contacts.toSave")} {problem.join(" ")}
          </p>
        )}
        <div className="c-row cl-new-actions">
          <Button variant="secondary" onClick={cancel}>{portalText("operations", "cancel_77dfd213")}{" "}</Button>
          <Button
            variant="primary"
            disabled={!saveable}
            aria-describedby={problem.length === 0 ? undefined : saveNoteId}
            onClick={() => {
              create.mutate();
            }}
          >
            {create.isPending ? portalText("operations", "saving_56a2285c") : forCampaign ? portalText("operations", "save_and_use_in_campaign_c71371d2") : portalText("operations", "save_list_1364b58c")}
          </Button>
        </div>
      </div>

      <Dialog
        open={confirmDiscard}
        title={portalText("operations", "discard_this_list_e1ffef80")}
        onClose={() => {
          setConfirmDiscard(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmDiscard(false);
              }}
            >{portalText("operations", "keep_editing_d4d9e382")}{" "}</Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmDiscard(false);
                onClose();
              }}
            >{portalText("operations", "discard_36fff63c")}{" "}</Button>
          </>
        }
      >
        <p>{portalText("shell", "merge.contacts.discard")}</p>
      </Dialog>
    </div>
  );
}

/**
 * "Columns found" (export 2026-10-10): each column as written, an example and its tag — read by its name or
 * ignored — and whether the list can be called. Beneath, what the export does not draw and saving needs said:
 * how many people saving keeps, the rows it skips and why, and the time zones it cannot use.
 */
export function InspectionResult({
  column,
  counted,
  columnRefusal,
}: {
  readonly column: readonly ContactColumnView[];
  readonly counted: ContactListInspectionView | undefined;
  readonly columnRefusal: string | null;
}): ReactNode {
  usePortalTranslation("shell");
  const note = columnsNoteOf(column);
  const skipped = counted?.unreadableRow ?? [];
  const zone = (counted?.unrecognisedValue ?? []).filter((one) => one.kind === "unknown_time_zone");
  return (
    <div className="field">
      <span className="cl-field-label">{portalText("shell", "merge.contacts.columnsFound")}</span>
      {columnRefusal === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {columnRefusal}
        </p>
      )}
      <div className="cl-found">
        {foundColumnsOf(column).map((one) => (
          <div key={one.name} className="cl-found-row">
            <span className="cl-found-name">
              <span className="cl-found-source">{one.name}</span>
              {one.sample === null ? null : <span className="cl-found-sample">{portalText("shell", "merge.contacts.example")} {one.sample}</span>}
            </span>
            <span className={classes("tag", one.read ? "tag-accent" : "tag-outline", "cl-found-tag")}>{one.use}</span>
          </div>
        ))}
      </div>
      <span className={classes("cl-map-note", note.problem && "cl-map-note-problem")}>{note.text}</span>
      {note.problem || counted === undefined ? null : (
        <p className={skipped.length === 0 ? "c-note" : "c-note cl-note-warning"}>
          {readableSummaryText(counted.readableCount, counted.rowCount)}
        </p>
      )}
      {note.problem || skipped.length === 0 ? null : (
        <div className="cl-skipped">
          <table className="cl-skipped-table">
            <caption className="c-visually-hidden">{portalText("operations", "rows_that_will_not_be_saved_6e8e759a")}</caption>
            <thead>
              <tr>
                <th scope="col">{portalText("operations", "line_ea967600")}</th>
                <th scope="col">{portalText("operations", "name_709a2322")}</th>
                <th scope="col">{portalText("operations", "why_it_is_skipped_fec2c657")}</th>
              </tr>
            </thead>
            <tbody>
              {skipped.slice(0, SKIPPED_SHOWN).map((one) => (
                <tr key={`${String(one.line)}-${one.problem}-${one.displayName ?? ""}`}>
                  <td>{one.line}</td>
                  <td>{one.displayName ?? "—"}</td>
                  <td>
                    {one.code === "unknown_language"
                      ? `${one.problem}: "${counted?.unrecognisedValue.find((each) => each.line === one.line && each.kind === "unknown_language")?.value ?? ""}"`
                      : one.problem}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {skipped.length > SKIPPED_SHOWN ? (
            <p className="c-drop-hint">{portalText("shell", "merge.contacts.and")} {countText(skipped.length - SKIPPED_SHOWN)}{" "}{portalText("operations", "more_e7c95b4c")}</p>
          ) : null}
        </div>
      )}
      {note.problem || zone.length === 0 ? null : (
        <p className="c-note cl-note-warning">{portalText("shell", "merge.contacts.badZones")}{" "}
          {zone.slice(0, SKIPPED_SHOWN).map(unrecognisedText).join(" · ")}
          {zone.length > SKIPPED_SHOWN ? portalText("operations", "and_value_more_bb2adda6", { v0: countText(zone.length - SKIPPED_SHOWN) }) : ""}
        </p>
      )}
    </div>
  );
}
